import type { FC } from 'react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { Link } from '@compat/router';
import { CheckBox, Picture } from '@ui';
import { arrowMedium } from '@images';
import useWindowSize from '@customHooks/useWindowSize';
import { observer } from 'mobx-react';
import './index.scss';

const sortlistRowTemple = [
  { title: 'Дате', type: 'date', asc: 'dateNew', desc: 'dateOld' },
  {
    title: 'Рейтингу',
    type: 'rating',
    asc: 'ratingHigh',
    desc: 'ratingLow'
  },
  { title: 'Полезности', type: 'useful', asc: 'useful', desc: 'useful' }
];

interface SortListProps {
  sortlist: Array<{ id?: string; param: string; title: string; url: string }>;
  value: number | string;
  callback?: (param: string, id?: string | number) => void;
  link?: boolean;
  sortlistInRow?: boolean;
  isQuestionTab?: boolean;
  showWithFileBtn?: boolean;
  onWithFileToggler?: () => void;
  withFile?: boolean;
}

const SortList: FC<SortListProps> = ({
  sortlist,
  value,
  callback,
  link,
  sortlistInRow,
  isQuestionTab,
  showWithFileBtn,
  onWithFileToggler,
  withFile
}) => {
  const { isMobile, isDesktop } = useWindowSize();
  const [isOpened, setIsOpened] = useState(false);
  const [directionAsc, setDirectionAsc] = useState({
    date: true,
    rating: false,
    useful: false
  });

  const showFilterList = useCallback(() => {
    setIsOpened(prev => !prev);
  }, []);

  const hideFilterListDesktop = useCallback(() => {
    setIsOpened(false);
  }, []);

  const hideFilterListMobile = useCallback(() => {
    hideFilterListDesktop();

    const blockToScroll = document.getElementById('scroll-here');
    setTimeout(
      () => blockToScroll?.scrollIntoView?.({ behavior: 'smooth' }),
      500
    );
  }, [hideFilterListDesktop]);

  const showFilterListHover = useCallback(() => {
    setIsOpened(true);
  }, []);

  const onSortDirection = useCallback(
    item => {
      setDirectionAsc(prev => ({
        ...prev,
        [item.type]: !prev[item.type]
      }));
      callback?.(directionAsc[item.type] ? item.asc : item.desc);
    },
    [callback, directionAsc]
  );

  const sortlistRow = useMemo(
    () => (
      <div className='sort-in-row'>
        <span className='sort-in-row__title'>Сортировать по:</span>
        <ul>
          {sortlistRowTemple?.map((item, index) => (
            <li
              key={index}
              onClick={() => {
                onSortDirection(item);
              }}
              className={`${item.type}${
                isQuestionTab && item.type === ' rating' ? ' hide' : ''
              }${item.type === value ? ' active' : ''}${
                item.asc === value ? ' active-desc' : ''
              }${item.desc === value ? ' active-asc' : ''}`}
            >
              <span>{item.title}</span>
              <Picture icon={arrowMedium} height='16' />
            </li>
          ))}
        </ul>
        {showWithFileBtn && (
          <div onClick={onWithFileToggler} className='sort-in-row__only-file'>
            <CheckBox isChecked={withFile} />
            Только с фото
          </div>
        )}
      </div>
    ),
    [
      isQuestionTab,
      onSortDirection,
      onWithFileToggler,
      showWithFileBtn,
      value,
      withFile
    ]
  );

  const filterHeader = useMemo(() => {
    const i = sortlist.find(item => item.param === value);
    return (
      <div className='header-accordion'>
        <span>{i ? i.title : 'Все'}</span>
        <i className='accordion-control' />
      </div>
    );
  }, [sortlist, value]);

  const filterList = useMemo(
    () => (
      <ul>
        {sortlist?.map((item, index) => {
          const elem = link ? (
            <Link
              key={index}
              to={item.url}
              className={value === item.param ? 'sortItem active' : 'sortItem'}
              onClick={() => callback?.(item.param, item?.id)}
            >
              {item.title}
            </Link>
          ) : (
            <li
              key={index}
              className={value === item.param ? ' sortItem active' : 'sortItem'}
              onClick={() => callback?.(item.param, item?.id)}
            >
              {item.title}
            </li>
          );
          return elem;
        })}
      </ul>
    ),
    [callback, link, sortlist, value]
  );

  const filterDesktop = useMemo(
    () => (
      <div
        className={`sort-container ${isOpened ? 'show' : ''}`}
        onMouseOver={showFilterListHover}
        onMouseLeave={hideFilterListDesktop}
      >
        {filterHeader}
        {isOpened && (
          <div className='sort-list-container'>
            {isOpened && (
              <div className='sort-list' onClick={hideFilterListDesktop}>
                {filterList}
              </div>
            )}
          </div>
        )}
      </div>
    ),
    [
      filterHeader,
      filterList,
      hideFilterListDesktop,
      isOpened,
      showFilterListHover
    ]
  );
  //
  const filterMobile = useMemo(
    () => (
      <div
        className={`sort-container ${isOpened ? 'show' : ''}`}
        onClick={showFilterList}
      >
        {filterHeader}
        <div className='sort-list-container'>
          {isOpened && (
            <div className='sort-list' onClick={hideFilterListMobile}>
              {filterList}
            </div>
          )}
        </div>
      </div>
    ),
    [filterHeader, filterList, hideFilterListMobile, isOpened, showFilterList]
  );

  const content = useMemo(
    () => (
      <Fragment>
        {isDesktop && !sortlistInRow && filterDesktop}
        {isMobile && !sortlistInRow && filterMobile}
        {isDesktop && sortlistInRow && sortlistRow}
        {isMobile && sortlistInRow && sortlistRow}
      </Fragment>
    ),
    [
      filterDesktop,
      filterMobile,
      isDesktop,
      isMobile,
      sortlistInRow,
      sortlistRow
    ]
  );

  return content;
};

export default observer(SortList);
